import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useScrollReveal as useGsapReveal } from '../../hooks/useScrollReveal';
import { PROGRAMS } from '../../data/programs';

function useScrollReveal() {
  const refs = useRef([]);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('card-hidden');
            entry.target.classList.add('card-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    refs.current.forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);
  return refs;
}

export default function ServicesSection() {
  const [hoveredId, setHoveredId] = useState(null);
  const cardRefs = useScrollReveal();
  const headerRef = useGsapReveal('.services-reveal', { y: 28, stagger: 0.15 });

  return (
    <section id="services" data-testid="services-section" className="pt-24 lg:pt-32 pb-12 lg:pb-16 relative overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #f0f4ff 0%, #f7f9fc 50%, #f0f7ff 100%)' }}>

        {/* Decorative blobs */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-100/40 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-100/40 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">

          {/* ── Header ── */}
          <div ref={headerRef} className="text-center mb-16">
            <div className="services-reveal inline-flex items-center gap-2 bg-[#0056D2]/10 text-[#0056D2] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
              <Zap className="w-3.5 h-3.5" />
              What We Offer
            </div>
            <h2 className="services-reveal font-heading font-black text-4xl md:text-5xl text-[#0F1F3D] mb-4 leading-tight">
              Training Programs
            </h2>
            <p className="services-reveal font-body text-base text-[#5A6A85] max-w-xl mx-auto leading-relaxed">
              Industry-aligned programs designed by experts to help you master in-demand technologies and land your dream job.
            </p>
            {/* Animated underline */}
            <div className="services-reveal mt-5 flex justify-center">
              <div className="h-1 w-16 rounded-full bg-[#0056D2]" style={{ boxShadow: '0 0 12px #0056D2aa' }} />
            </div>
          </div>

          {/* ── Cards Grid ── */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {PROGRAMS.map((program, i) => {
              const Icon = program.icon;
              const isHovered = hoveredId === program.id;
              return (
                <div
                  key={program.id}
                  ref={el => cardRefs.current[i] = el}
                  data-testid={`service-card-${program.id}`}
                  className="card-hidden rounded-2xl overflow-hidden flex flex-col cursor-pointer"
                  style={{
                    animationDelay: `${i * 0.1}s`,
                    background: '#fff',
                    boxShadow: isHovered
                      ? `0 20px 60px -10px ${program.accent}44, 0 4px 24px rgba(0,0,0,0.08)`
                      : '0 2px 16px rgba(0,0,0,0.06)',
                    transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
                    transition: 'box-shadow 0.35s ease, transform 0.35s ease',
                    border: isHovered ? `1.5px solid ${program.accent}33` : '1.5px solid #EEF1F6',
                  }}
                  onMouseEnter={() => setHoveredId(program.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  {/* ── Image with overlay ── */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={program.image}
                      alt={program.title}
                      className="w-full h-full object-cover"
                      style={{
                        transform: isHovered ? 'scale(1.08)' : 'scale(1)',
                        transition: 'transform 0.5s ease',
                      }}
                    />
                    {/* gradient overlay */}
                    <div
                      className="absolute inset-0 transition-opacity duration-300"
                      style={{
                        background: isHovered
                          ? `linear-gradient(to top, ${program.accent}ee 0%, ${program.accent}44 60%, transparent 100%)`
                          : `linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 60%, transparent 100%)`,
                      }}
                    />
                    {/* Badge */}
                    <div
                      className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-xs font-bold backdrop-blur-sm"
                      style={{ backgroundColor: program.accent + 'CC' }}
                    >
                      <Icon className="w-3 h-3" />
                      {program.title}
                    </div>
                    {/* Title on image bottom */}
                    <div className="absolute bottom-3 left-4 right-4">
                      <h3 className="font-heading font-bold text-white text-lg leading-tight drop-shadow-lg">
                        {program.title}
                      </h3>
                    </div>
                  </div>

                  {/* ── Body ── */}
                  <div className="p-5 flex flex-col flex-1">
                    <p className="font-body text-sm text-[#5A6A85] leading-relaxed mb-4">
                      {program.description}
                    </p>

                    {/* Topic pills */}
                    <div className="flex flex-wrap gap-1.5 mb-5 mt-auto">
                      {program.topics.map(topic => (
                        <span
                          key={topic}
                          className="text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors duration-200"
                          style={{
                            backgroundColor: isHovered ? program.accent + '18' : program.bg,
                            color: program.accent,
                          }}
                        >
                          {topic}
                        </span>
                      ))}
                    </div>

                    {/* CTA */}
                    <Link
                      to={`/programs/${program.slug}`}
                      className="group/btn flex items-center justify-between w-full px-4 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200"
                      style={{
                        backgroundColor: isHovered ? program.accent : program.bg,
                        color: isHovered ? '#fff' : program.accent,
                      }}
                    >
                      <span>View Program Details</span>
                      <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover/btn:translate-x-1" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
    </section>
  );
}
